import { useState } from "react";
import { Input } from "@/components/ui/input";
import { TONE_LABEL } from "./FoodCostUI";

const F = ({ label, children }) => <label className="flex flex-col gap-1 text-sm font-semibold text-[#5a3a22]">{label}{children}</label>;
const RO = ({ v, testid }) => <span className="rounded-lg bg-[#fbf0d6] px-3 py-2 text-base font-mono text-[#3b0d0d]" data-testid={testid}>{v}</span>;

export function ConsumptionItemCards({ rows, update, compute, canEdit, fmt, total }) {
  const [limit, setLimit] = useState(48);
  const [open, setOpen] = useState(null);
  if (!rows.length) return <p className="bi-panel text-base text-muted-foreground">No items in this category.</p>;
  return (
    <div className="space-y-3" data-testid="fc-item-cards">
      <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.slice(0, limit).map((r) => {
          const c = compute(r);
          const exp = open === r.item_code;
          const num = (f) => (
            <Input type="number" step="0.01" value={r[f]} disabled={!canEdit} className="h-11 text-base font-mono"
              onChange={(e) => update(r.__i, f, parseFloat(e.target.value) || 0)} data-testid={`fc-card-${f}-${r.item_code}`} />);
          return (
            <div key={r.item_code} className={`rounded-2xl border-2 bg-white p-4 shadow-sm ${c.pending ? "border-amber-300" : "border-[#ead6a6]"}`} data-testid={`fc-card-${r.item_code}`}>
              <button type="button" className="flex w-full items-start justify-between gap-2 text-left" onClick={() => setOpen(exp ? null : r.item_code)} data-testid={`fc-card-open-${r.item_code}`}>
                <span><span className="block text-lg font-bold text-[#750817]">{r.item_name}</span><span className="text-sm text-muted-foreground">{r.item_code} · {r.unit} · {r.category}</span></span>
                <span className="text-right"><span className="block text-sm text-muted-foreground">Closing</span><span className="font-mono text-lg font-bold" data-testid={`fc-card-closing-${r.item_code}`}>{c.closing.toFixed(2)}</span></span>
              </button>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-[#fbf0d6] px-3 py-1">Used {c.totalQty.toFixed(2)}</span>
                <span className="rounded-full bg-[#fbf0d6] px-3 py-1">Cost {fmt(c.cost)}</span>
                <span className={`rounded-full px-3 py-1 font-semibold ${c.pending ? "bg-amber-400 text-[#3b1d00]" : "bg-emerald-600 text-white"}`}>{c.pending ? "Rate pending" : "Priced"}</span>
              </div>
              {exp && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <F label="Opening stock"><RO v={Number(r.opening_qty || 0).toFixed(2)} /></F>
                  <F label="Received qty">{num("qty_received")}</F>
                  <F label="Staff consumption">{num("staff_qty")}</F>
                  <F label="Guest consumption">{num("guest_qty")}</F>
                  <F label="Wastage (from Wastage)"><RO v={Number(r.wastage_qty || 0).toFixed(2)} /></F>
                  <F label="Total consumption"><RO v={c.totalQty.toFixed(2)} testid={`fc-card-total-${r.item_code}`} /></F>
                  <F label="Rate"><RO v={fmt(r.unit_price)} /></F>
                  <F label="Cost"><RO v={fmt(c.cost)} /></F>
                  <div className="col-span-2"><F label="Remarks"><Input value={r.remarks || ""} disabled={!canEdit} className="h-11 text-base" onChange={(e) => update(r.__i, "remarks", e.target.value)} data-testid={`fc-card-remarks-${r.item_code}`} /></F></div>
                </div>)}
            </div>);
        })}
      </div>
      {rows.length > limit && <button type="button" className="bi-pill" onClick={() => setLimit(limit + 48)} data-testid="fc-cards-more">Show more ({rows.length - limit} left)</button>}
      {total}
    </div>
  );
}

export function PlannerItemCards({ rows, sym, manualQty, setManualQty, selected, setSelected, isManualRow, manualReason, setManualReason, reasonOptions }) {
  const [limit, setLimit] = useState(48);
  if (!rows.length) return <p className="bi-panel text-base text-muted-foreground">No items in this category.</p>;
  const tone = (c) => (c === "red" || c === "orange" ? "red" : c === "yellow" ? "amber" : c === "green" ? "green" : "amber");
  const CLS = { red: "border-red-300 bg-red-50", amber: "border-[#e2b85a] bg-[#fff7e3]", green: "border-emerald-300 bg-emerald-50" };
  const PILL = { red: "bg-red-600 text-white", amber: "bg-amber-400 text-[#3b1d00]", green: "bg-emerald-600 text-white" };
  return (
    <div className="space-y-3" data-testid="pp-item-cards">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.slice(0, limit).map((r) => {
          const t = tone(r.color);
          const kv = (l, v) => <div className="flex justify-between gap-2 text-base"><span className="text-[#5a3a22]">{l}</span><b className="font-mono">{v}</b></div>;
          return (
            <div key={r.item_code} className={`rounded-2xl border-2 p-4 ${CLS[t]}`} data-testid={`pp-card-${r.item_code}`}>
              <div className="flex items-start justify-between gap-2">
                <span><span className="block text-lg font-bold text-[#750817]">{r.item_name}</span><span className="text-sm text-muted-foreground">{r.item_code} · {r.unit} · {r.category}</span></span>
                <span className={`rounded-full px-3 py-1 text-sm font-semibold ${PILL[t]}`}>{TONE_LABEL[r.color] || r.status || "Review"}</span>
              </div>
              <div className="mt-3 space-y-1">
                {kv("Current stock", r.current_stock)}{kv("Expected consumption", r.expected_consumption)}{kv("Safety stock", r.safety_stock)}
                {kv("Suggested purchase", r.recommended_purchase_qty)}{kv("Avg rate (WAP)", `${sym}${Number(r.current_wap || 0).toLocaleString()}`)}
                {kv("Estimated cost", `${sym}${Number(r.estimated_purchase_value || 0).toLocaleString()}`)}{kv("Vendor", r.preferred_vendor || "—")}
              </div>
              {r.reason && <p className="mt-2 text-sm text-[#5a3a22]">{r.reason}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input type="number" step="0.01" min="0" placeholder="Override qty" value={manualQty[r.item_code] ?? ""} className="h-11 w-36 text-base font-mono"
                  onChange={(e) => setManualQty((m) => ({ ...m, [r.item_code]: e.target.value }))} data-testid={`pp-card-qty-${r.item_code}`} />
                <label className="flex items-center gap-2 text-base"><input type="checkbox" className="h-5 w-5" checked={!!selected[r.item_code]}
                  onChange={(e) => setSelected((s) => ({ ...s, [r.item_code]: e.target.checked }))} data-testid={`pp-card-sel-${r.item_code}`} />Add to PO</label>
              </div>
              {isManualRow?.(r) && (() => {
                const rk = manualReason[r.item_code] || {};
                const set = (k, v) => setManualReason((m) => ({ ...m, [r.item_code]: { ...(m[r.item_code] || {}), [k]: v } }));
                return (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <select className="h-11 rounded-md border bg-white px-2 text-base" value={rk.kind || ""} onChange={(e) => set("kind", e.target.value)} data-testid={`pp-card-reason-${r.item_code}`}>
                      <option value="">— pick reason (required) —</option>{reasonOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {rk.kind === "Other" && <Input className="h-11 w-48 text-base" placeholder="Remark (min 3 chars)" value={rk.text || ""} onChange={(e) => set("text", e.target.value)} data-testid={`pp-card-reason-text-${r.item_code}`} />}
                  </div>);
              })()}
            </div>);
        })}
      </div>
      {rows.length > limit && <button type="button" className="bi-pill" onClick={() => setLimit(limit + 48)}>Show more ({rows.length - limit} left)</button>}
    </div>
  );
}
